import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useFetchAndFilterPersonsPool from 'hooks/queries/useFetchAndFilterPersonsPool';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import Loader from 'components/loader/Loader';
import VirtualizedEntityList from 'containers/pages/persons/VirtualizedEntityList';
import * as Sentry from '@sentry/react';
import usePersonStatus from 'hooks/utils/usePersonStatus';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import { useContextMenu } from 'contexts/ContextMenuProvider';
import { ButtonExpandable } from 'components/button';
import messages from 'messages';
import { FormattedMessage, useIntl } from 'react-intl';
import CreatePersonModal from 'containers/pages/persons/CreatePersonModal';
import Button from 'components/button/Button';
import Icon from 'components/icon/Icon';
import { ROUTE_PERSONS_ENTITY_POOL } from 'routes/RouteList';
import RemovePersonsFromOrgButton from 'containers/partials/buttons/RemovePersonsFromOrgButton';
import CopyPersonsModal, {
  PERSON_ACTION
} from 'containers/partials/modals/CopyPersonsModal';
import GenerateQRCodeModal from 'containers/partials/modals/GenerateQRCodeModal';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import {
  createEntityStore,
  EntityStoreContext
} from 'containers/pages/persons/store/EntityStoreContext';
import InviteSportersButton from 'containers/partials/buttons/InviteSportersButton';
import { MenuItemsGroup, MenuItemsGroupItem } from 'components/menu';
import ImportSportersCard, {
  IMPORT_PERSONS_STATE
} from 'containers/partials/cards/ImportSportersCard';
import Modal from 'components/modal/Modal';
import { StoreContext } from 'index';
import SyncPersonsWithIntegrationCard from 'containers/pages/persons/components/SyncPersonsWithIntegrationCard';

const modals = {
  COPY: 'copy',
  MOVE: 'move',
  IMPORT: 'import',
  PERSON: 'person',
  ADD_PERSON: 'add_person',
  GROUP: 'group',
  SYNC_INTEGRATION: 'sync_integration'
};

function PersonsPoolTab({
  excludedPersons = [],
  onModal = false,
  onClick,
  showPersonStatus = false
}) {
  const {
    authStore: { user }
  } = useContext(StoreContext);
  const intl = useIntl();
  const ability = useAbility(AbilityContext);
  const { push } = useHistory();
  const { entityId, groupId, personId } = useParams();

  const group = useEntityContext(s => s.group);
  const persons = useEntityContext(s => s.persons);
  const resetFilter = useEntityContext(s => s.resetFilter);
  const clearSelected = useEntityContext(s => s.clearSelected);
  const clearGroups = useEntityContext(s => s.setGroups);

  const selectedPersons = useEntityContext(s => s.selectedPersons);

  const entityModalStore = useRef(createEntityStore()).current;

  const { setMenuItems, addActionButton, clearAllContext } = useContextMenu();
  const [activeModal, setActiveModal] = useState({ type: null, action: null });
  const [showQrModal, setShowQrModal] = useState(false);
  const [modalSize, setModalSize] = useState(true);

  const { loading, fetchPool } = useFetchAndFilterPersonsPool({
    entityId
  });
  const { personStatusHandler } = usePersonStatus(entityId, groupId);

  useEffect(() => {
    const loadPool = async () => {
      await fetchPool({ withEntity: true });
    };
    loadPool().catch(error => {
      Sentry.captureException(error);
    });
    clearGroups([]);
    return () => {
      resetFilter();
      clearSelected();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ImportPersonsBtn = ({ dataQa }) => {
    return (
      <Button
        key={'importPersons'}
        menu
        onClick={() => setActiveModal({ type: modals.IMPORT, action: 'add' })}
        dataQa={dataQa}
      >
        <FormattedMessage {...messages.buttonSportersImportSporters} />
        <Icon id="add-user" />
      </Button>
    );
  };

  const CopyPersonsFromPoolBtn = () => (
    <CopyPersonsFromPool
      key={'copySporters'}
      onClick={() =>
        setActiveModal({
          type: modals.COPY,
          action: PERSON_ACTION.COPY_POOL,
          selected: selectedPersons
        })
      }
    />
  );

  const GenerateQRCodeBtn = () => (
    <Button
      onClick={() => setShowQrModal(true)}
      menu
      disabled={selectedPersons.length === 0}
      dataQa={'generate-qr-code'}
      title={
        selectedPersons.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedPersonsTooltip)
          : null
      }
    >
      <FormattedMessage {...messages.generateQrCode} />
      <Icon id="qr-code" />
    </Button>
  );

  const SyncPersonsWithIntegrationBtn = ({ integration }) => (
    <Button
      menu
      onClick={() =>
        setActiveModal({
          type: modals.SYNC_INTEGRATION,
          action: 'sync',
          integration
        })
      }
      dataQa={'persons-pool-context-sync'}
    >
      <FormattedMessage
        {...messages.buttonSportersSyncPersons}
        values={{ integration: 'Vald' }}
      />
      <Icon id="add-user" />
    </Button>
  );

  useEffect(() => {
    const contextButtons = [];

    contextButtons.push(
      <MenuItemsGroup key={'menuItemsGroup'}>
        <MenuItemsGroupItem>
          <ImportPersonsBtn
            key={'importPersons'}
            dataQa={'persons-pool-context-import'}
          />
        </MenuItemsGroupItem>
      </MenuItemsGroup>
    );

    if (ability.can('manage', 'ValdIntegration')) {
      contextButtons.push(
        <MenuItemsGroup key={'menuItemsGroupSync'}>
          <SyncPersonsWithIntegrationBtn
            key={'syncPersonsWithIntegration'}
            integration="vald"
          />
        </MenuItemsGroup>
      );
    }

    contextButtons.push(
      <CopyPersonsFromPoolBtn key={'generateQrCode'} />,
      <RemovePersonsFromOrg key={'removeSporters'} entityId={entityId} />,
      <InviteSporters key={'inviteSporters'} entityId={entityId} />
    );

    if (ability.can('manage', 'AutomatedMeasurementProgram')) {
      contextButtons.push(<GenerateQRCodeBtn />);
    }

    setMenuItems(contextButtons);
    addActionButton(
      <ButtonExpandable
        tabIndex={0}
        onClick={() => setActiveModal({ type: modals.PERSON, action: 'add' })}
        label={intl.formatMessage(messages.buttonSportersAddSporter)}
      />
    );

    return () => {
      clearAllContext();
    };
  }, [entityId, persons, selectedPersons]);

  const onImportModalStateChange = state => {
    setModalSize(state !== IMPORT_PERSONS_STATE.RESULT);
  };

  const onCloseModal = async () => {
    setActiveModal({ type: null, action: null });
  };

  const onDoneModal = async (entityIds, navigate) => {
    setActiveModal({ type: null, action: null });

    clearSelected();

    if (entityIds && entityIds?.length > 0) {
      for await (const id of entityIds) {
        await fetchPool({ withEntity: true });
      }
    }

    if (navigate) {
      push(navigate);
    }
  };

  if (loading) return <Loader />;

  return (
    <>
      <VirtualizedEntityList
        id={'pool'}
        onModal={onModal}
        items={[...persons]}
        checkable
        showPersonStatus={showPersonStatus}
        activeId={personId}
        onClick={person => {
          clearSelected();
          onClick && onClick(person, 'person');
        }}
        onClickPersonStatus={personStatusHandler}
        excludedItems={excludedPersons}
      />

      {activeModal.type === modals.PERSON && (
        <CreatePersonModal onClose={onCloseModal} onDone={onDoneModal} />
      )}
      {activeModal.type === modals.COPY && (
        <EntityStoreContext.Provider value={entityModalStore}>
          <CopyPersonsModal
            oldEntityId={groupId === 'all' ? entityId : groupId}
            selected={selectedPersons}
            excludedGroups={[groupId]}
            onClose={onCloseModal}
            onDone={onDoneModal}
            action={activeModal.action}
          />
        </EntityStoreContext.Provider>
      )}

      {activeModal.type === modals.IMPORT && (
        <Modal
          importSporters={!modalSize}
          cardSmall={modalSize}
          autoHeight={modalSize}
          isOpen={true}
          onClose={onCloseModal}
        >
          <ImportSportersCard
            rootEntityId={user.rootEntityId}
            entityId={groupId === 'all' ? entityId : groupId}
            entityName={group.name}
            onClose={reload => onCloseModal(reload)}
            onStateChange={onImportModalStateChange}
          />
        </Modal>
      )}

      {activeModal.type === modals.SYNC_INTEGRATION && (
        <Modal
          importSporters
          cardSmall
          autoHeight={modalSize}
          isOpen={true}
          onClose={onCloseModal}
        >
          <SyncPersonsWithIntegrationCard
            rootEntityId={user.rootEntityId}
            entityId={groupId === 'all' ? entityId : groupId}
            entityName={group.name}
            integration={activeModal.integration}
            onClose={reload => onCloseModal(reload)}
            onStateChange={onImportModalStateChange}
          />
        </Modal>
      )}

      {showQrModal && (
        <GenerateQRCodeModal
          onClose={() => setShowQrModal(false)}
          persons={selectedPersons}
          type="session"
          entityId={entityId}
        />
      )}
    </>
  );
}

export default PersonsPoolTab;

const CopyPersonsFromPool = ({ onClick }) => {
  const intl = useIntl();
  const selected = useEntityContext(s => s.selectedPersons);
  return (
    <Button
      disabled={selected.length <= 0}
      title={
        selected.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedPersonsTooltip)
          : null
      }
      menu
      onClick={onClick}
    >
      <FormattedMessage
        {...messages.buttonSportersCopySportersFromPool}
        values={{
          count: selected.length === 0 ? 1 : selected.length
        }}
      />
      <Icon id="copy" />
    </Button>
  );
};

const InviteSporters = ({ entityId }) => {
  const intl = useIntl();
  const selected = useEntityContext(s => s.selectedPersons);
  return (
    <InviteSportersButton
      disabled={selected.length <= 0}
      title={
        selected.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedPersonsTooltip)
          : null
      }
      menu
      entityId={entityId}
      persons={selected}
    >
      <FormattedMessage
        {...messages.buttonSportersInvite}
        values={{
          count: selected.length === 0 ? 1 : selected.length
        }}
      />
    </InviteSportersButton>
  );
};

const RemovePersonsFromOrg = ({ entityId }) => {
  const intl = useIntl();
  const { replace } = useHistory();
  const selected = useEntityContext(s => s.selectedPersons);
  const clearSelected = useEntityContext(s => s.clearSelected);

  const onPersonsRemovedFromOrg = useCallback(async () => {
    /*await getPool({
      variables: { entityId },
      fetchPolicy: 'network-only'
    });*/
    clearSelected();
    replace(
      generatePath(ROUTE_PERSONS_ENTITY_POOL, { entityId, groupId: 'all' })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSelected]);

  return (
    <RemovePersonsFromOrgButton
      disabled={selected.length <= 0}
      title={
        selected.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedPersonsTooltip)
          : null
      }
      entityId={entityId}
      persons={selected}
      menu
      onComplete={onPersonsRemovedFromOrg}
    />
  );
};
